/**
 * The preset cards' view model, and the one comparison made on it.
 *
 * TWO PLACES ANSWER "what would this preset cost?" and they answer it from
 * different shapes. The cart row carries a `CartPresetOut` per preset,
 * computed by the server when the cart was read; `POST /quotes/options`
 * answers with an `OptionPriceOut`, which prices the whole order once per
 * preset. Both collapse to the same handful of facts a card draws, so the
 * picker takes those facts and neither of the two shapes.
 */
import type { CartItemOut, CartPresetOut, OptionPriceOut, PrintOptionOut } from '@/lib/api/types';

/** One preset card's worth of fact. Everything on it is already decided. */
export interface PresetChoice {
  id: string;
  slug: string;
  label: string;
  description: string | null;
  pricePerPagePaise: number;
  /** Sheets in the whole job, all copies. */
  sheets: number;
  /** Pages x copies x the rate. Printing alone, before handling and tax. */
  printingCostPaise: number;
  /** The paper this preset prints on. */
  sizeLabel: string;
  /** The customer's own size, when it is not the paper above. */
  documentSizeLabel: string | null;
}

/** Pages x copies x the per-page rate. Printing alone. */
export function printingCostPaise(
  entry: OptionPriceOut,
  pageCount: number,
  copies: number,
): number {
  return pageCount * copies * entry.option.price_per_page_paise;
}

/** A priced option from `/quotes/options`, as a card. */
export function choiceFromPrice(
  entry: OptionPriceOut,
  pageCount: number,
  copies: number,
): PresetChoice {
  return {
    id: entry.option.id,
    slug: entry.option.slug,
    label: entry.option.label,
    description: entry.option.description ?? null,
    pricePerPagePaise: entry.option.price_per_page_paise,
    sheets: entry.totals.sheets,
    printingCostPaise: printingCostPaise(entry, pageCount, copies),
    sizeLabel: entry.size_label,
    documentSizeLabel: entry.document_size_label,
  };
}

/**
 * A cart row's presets, as cards.
 *
 * The row names each preset by slug alone, so the catalogue supplies the
 * words. A preset the server priced but the catalogue has not got — a row
 * retired between the two reads — is dropped rather than drawn nameless.
 */
export function choicesFromCartItem(
  item: Pick<CartItemOut, 'presets'>,
  catalogue: readonly PrintOptionOut[],
): PresetChoice[] {
  const byslug = new Map(catalogue.map((option) => [option.slug, option]));
  return item.presets.flatMap((preset: CartPresetOut) => {
    const option = byslug.get(preset.slug);
    if (!option) return [];
    return [
      {
        id: option.id,
        slug: preset.slug,
        label: option.label,
        description: option.description ?? null,
        pricePerPagePaise: option.price_per_page_paise,
        sheets: preset.sheets,
        printingCostPaise: preset.printing_cost_paise,
        sizeLabel: preset.size_label,
        documentSizeLabel: preset.document_size_label,
      },
    ];
  });
}

/**
 * The cheapest preset, compared on the SAME number the cards show.
 *
 * Every preset is printable now — one that does not stock the customer's page
 * size prints on the nearest sheet it does stock rather than refusing — so
 * there is nothing to exclude before comparing.
 *
 * Today every preset carries the same handling fee, so ordering by printing
 * and ordering by the delivered total give the same answer. That is a fact
 * about the current price list, not a property of the code — the moment one
 * preset has a different fee, a badge computed from the total would sit on a
 * card that is not the smallest number on screen.
 */
export function cheapestSlug(choices: readonly PresetChoice[]): string | null {
  if (choices.length === 0) return null;
  return choices.reduce((best, choice) =>
    choice.printingCostPaise < best.printingCostPaise ? choice : best,
  ).slug;
}
